import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { deneignementService } from '@/services/deneignementService';
import { equipeService } from '@/services/equipeService';
import { useAuthStore } from '@/store/authStore';
import { useAuthorization } from '@/hooks/useAuthorization';
import { StatusBadge } from '@/components/common/StatusBadge';
import { PrioriteBadge } from '@/components/common/PrioriteBadge';
import { ConfirmModal } from '@/components/common/Modal';
import type { DeneignementFilters, StatutDeneignement, PrioriteDeneignement, Deneignement } from '@/types';
import { STATUT_LABELS, TYPE_LABELS, PRIORITE_LABELS } from '@/types';
import toast from 'react-hot-toast';
import { getErrorMessage } from '@/services/api';
import {
  FunnelIcon,
  PlusIcon,
  EyeIcon,
  PencilIcon,
  DocumentArrowDownIcon,
  UserGroupIcon,
  XMarkIcon,
  ClipboardDocumentListIcon,
  ChevronUpIcon,
  ChevronDownIcon,
  ChevronUpDownIcon,
  NoSymbolIcon,
  TrashIcon,
} from '@heroicons/react/24/outline';

/** Statuts pour lesquels une intervention peut encore etre annulee (R5) */
const STATUTS_ANNULABLES: StatutDeneignement[] = ['en_attente', 'affectee', 'en_deplacement'];

/** Statuts pour lesquels l'equipe deja affectee peut encore etre changee (reassignation) */
const STATUTS_EQUIPE_MODIFIABLE: StatutDeneignement[] = ['affectee', 'en_deplacement', 'en_cours'];

type SortField = NonNullable<DeneignementFilters['sortField']>;

/** En-tete de colonne cliquable pour trier (page Interventions) */
function SortableHeader({
  field,
  label,
  activeField,
  activeDirection,
  onSort,
}: {
  field: SortField;
  label: string;
  activeField?: SortField;
  activeDirection?: 'asc' | 'desc';
  onSort: (field: SortField) => void;
}) {
  const isActive = activeField === field;
  return (
    <th>
      <button
        type="button"
        onClick={() => onSort(field)}
        className="flex items-center gap-1 font-medium hover:text-primary-600 focus:outline-none"
      >
        <span>{label}</span>
        {isActive ? (
          activeDirection === 'desc' ? (
            <ChevronDownIcon className="w-4 h-4 text-primary-600" />
          ) : (
            <ChevronUpIcon className="w-4 h-4 text-primary-600" />
          )
        ) : (
          <ChevronUpDownIcon className="w-4 h-4 text-gray-300" />
        )}
      </button>
    </th>
  );
}

/**
 * Page liste des interventions de deneigement
 */
export function DeneignementListPage() {
  const [page, setPage] = useState(1);
  const [filters, setFilters] = useState<DeneignementFilters>({});
  const [showFilters, setShowFilters] = useState(false);

  // Tri sur une colonne : premier clic = ascendant, reclic sur la meme colonne =
  // inverse la direction, clic sur une autre colonne = ascendant sur la nouvelle
  const handleSort = (field: SortField) => {
    setFilters((prev) => ({
      ...prev,
      sortField: field,
      sortDirection: prev.sortField === field && prev.sortDirection === 'asc' ? 'desc' : 'asc',
    }));
    setPage(1);
  };
  const [showAffectationModal, setShowAffectationModal] = useState(false);
  const [selectedIntervention, setSelectedIntervention] = useState<Deneignement | null>(null);
  const [selectedEquipeId, setSelectedEquipeId] = useState<number | null>(null);
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [cancelMotif, setCancelMotif] = useState('');
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const queryClient = useQueryClient();
  const { user } = useAuthStore();
  const isAdmin = user?.roles.includes('ROLE_ADMIN') ?? false;
  const { canAccess } = useAuthorization();

  // Chargement des donnees
  const { data, isLoading, error } = useQuery({
    queryKey: ['deneignements', filters, page],
    queryFn: () => deneignementService.list(filters, page, 20),
  });

  // Compteur d'interventions non affectees, pour le raccourci de filtre rapide
  const { data: enAttenteCount } = useQuery({
    queryKey: ['deneignements', 'count-en-attente'],
    queryFn: () => deneignementService.list({ statut: 'en_attente' }, 1, 1),
    select: (res) => res.total,
    refetchInterval: 30000,
  });

  const filtreNonAffectees = filters.statut === 'en_attente';
  const toggleNonAffectees = () => {
    setFilters((prev) => ({
      ...prev,
      statut: prev.statut === 'en_attente' ? undefined : 'en_attente',
    }));
    setPage(1);
  };

  // Chargement des equipes
  const { data: equipes } = useQuery({
    queryKey: ['equipes'],
    queryFn: () => equipeService.list(),
  });

  // Mutation pour l'affectation initiale (en_attente -> affectee)
  const affectationMutation = useMutation({
    mutationFn: ({ interventionId, equipeId }: { interventionId: number; equipeId: number }) =>
      deneignementService.affecter(interventionId, { equipeId }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['deneignements'] });
      setShowAffectationModal(false);
      setSelectedIntervention(null);
      setSelectedEquipeId(null);
      toast.success('Equipe affectee avec succes');
    },
    onError: (error) => {
      toast.error(getErrorMessage(error));
    },
  });

  // Mutation pour changer l'equipe d'une intervention deja affectee (reassignation)
  const changerEquipeMutation = useMutation({
    mutationFn: ({ interventionId, equipeId }: { interventionId: number; equipeId: number }) =>
      deneignementService.changerEquipe(interventionId, { equipeId }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['deneignements'] });
      setShowAffectationModal(false);
      setSelectedIntervention(null);
      setSelectedEquipeId(null);
      toast.success('Equipe changee avec succes');
    },
    onError: (error) => {
      toast.error(getErrorMessage(error));
    },
  });

  const isChangementEquipe = selectedIntervention !== null && selectedIntervention.statut !== 'en_attente';

  const handleAffectation = () => {
    if (!selectedIntervention || !selectedEquipeId) {
      return;
    }
    const mutation = isChangementEquipe ? changerEquipeMutation : affectationMutation;
    mutation.mutate({
      interventionId: selectedIntervention.id,
      equipeId: selectedEquipeId,
    });
  };

  const openAffectationModal = (intervention: Deneignement) => {
    setSelectedIntervention(intervention);
    // Pre-remplit avec l'equipe actuelle si on change une affectation existante
    setSelectedEquipeId(intervention.equipes[0]?.id ?? null);
    setShowAffectationModal(true);
  };

  // Mutation pour l'annulation (R5 : uniquement avant debut des travaux)
  const cancelMutation = useMutation({
    mutationFn: ({ id, motif }: { id: number; motif: string }) =>
      deneignementService.annuler(id, { motif }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['deneignements'] });
      setShowCancelModal(false);
      setSelectedIntervention(null);
      setCancelMotif('');
      toast.success('Intervention annulee');
    },
    onError: (error) => {
      toast.error(getErrorMessage(error));
    },
  });

  const openCancelModal = (intervention: Deneignement) => {
    setSelectedIntervention(intervention);
    setCancelMotif('');
    setShowCancelModal(true);
  };

  // Mutation pour la suppression definitive (ROLE_ADMIN uniquement)
  const deleteMutation = useMutation({
    mutationFn: (id: number) => deneignementService.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['deneignements'] });
      setShowDeleteModal(false);
      setSelectedIntervention(null);
      toast.success('Intervention supprimee');
    },
    onError: (error) => {
      toast.error(getErrorMessage(error));
    },
  });

  const openDeleteModal = (intervention: Deneignement) => {
    setSelectedIntervention(intervention);
    setShowDeleteModal(true);
  };

  if (isLoading) {
    return (
      <div className="flex flex-col items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-500" />
        <p className="text-sm text-gray-500 mt-3">Chargement des interventions...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-12">
        <p className="text-red-500">Erreur lors du chargement des interventions</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h2 className="text-2xl font-bold text-gray-900">
          Interventions de deneigement
        </h2>
        <div className="flex items-center space-x-3">
          <button
            onClick={toggleNonAffectees}
            className={
              filtreNonAffectees
                ? 'inline-flex items-center px-4 py-2 rounded-lg font-medium bg-orange-600 text-white hover:bg-orange-700'
                : 'inline-flex items-center px-4 py-2 rounded-lg font-medium bg-orange-50 text-orange-700 hover:bg-orange-100'
            }
            title="N'afficher que les interventions pas encore affectees a une equipe"
          >
            <UserGroupIcon className="w-5 h-5 mr-2" />
            Non affectees
            {typeof enAttenteCount === 'number' && (
              <span
                className={
                  filtreNonAffectees
                    ? 'ml-2 px-1.5 py-0.5 text-xs rounded-full bg-white text-orange-700'
                    : 'ml-2 px-1.5 py-0.5 text-xs rounded-full bg-orange-600 text-white'
                }
              >
                {enAttenteCount}
              </span>
            )}
          </button>
          <button
            onClick={() => setShowFilters(!showFilters)}
            className="btn-secondary"
          >
            <FunnelIcon className="w-5 h-5 mr-2" />
            Filtres
          </button>
          {canAccess.creationDeneignement && (
            <Link to="/deneignements/nouveau" className="btn-primary">
              <PlusIcon className="w-5 h-5 mr-2" />
              Nouveau deneigement
            </Link>
          )}
        </div>
      </div>

      {/* Filtres */}
      {showFilters && (
        <div className="card">
          <div className="card-body">
            <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
              <div>
                <label className="label">Statut</label>
                <select
                  className="input"
                  value={filters.statut || ''}
                  onChange={(e) =>
                    setFilters({
                      ...filters,
                      statut: e.target.value as StatutDeneignement || undefined,
                    })
                  }
                >
                  <option value="">Tous les statuts</option>
                  {Object.entries(STATUT_LABELS).map(([value, label]) => (
                    <option key={value} value={value}>
                      {label}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="label">Date debut</label>
                <input
                  type="date"
                  className="input"
                  value={filters.dateDebut || ''}
                  onChange={(e) =>
                    setFilters({ ...filters, dateDebut: e.target.value || undefined })
                  }
                />
              </div>
              <div>
                <label className="label">Date fin</label>
                <input
                  type="date"
                  className="input"
                  value={filters.dateFin || ''}
                  onChange={(e) =>
                    setFilters({ ...filters, dateFin: e.target.value || undefined })
                  }
                />
              </div>
              <div>
                <label className="label">Priorite</label>
                <select
                  className="input"
                  value={filters.priorite || ''}
                  onChange={(e) =>
                    setFilters({
                      ...filters,
                      priorite: e.target.value as PrioriteDeneignement || undefined,
                    })
                  }
                >
                  <option value="">Toutes les priorites</option>
                  {Object.entries(PRIORITE_LABELS).map(([value, label]) => (
                    <option key={value} value={value}>
                      {label}
                    </option>
                  ))}
                </select>
              </div>
              <div className="flex items-end">
                <button
                  onClick={() => setFilters({})}
                  className="btn-secondary w-full"
                >
                  Reinitialiser
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Tableau */}
      <div className="card overflow-hidden">
        <div className="overflow-x-auto">
          <table className="table">
            <thead>
              <tr>
                <SortableHeader field="numeroOrdre" label="N° Ordre" activeField={filters.sortField} activeDirection={filters.sortDirection} onSort={handleSort} />
                <SortableHeader field="dateIntervention" label="Date / Heure" activeField={filters.sortField} activeDirection={filters.sortDirection} onSort={handleSort} />
                <SortableHeader field="compagnie" label="Compagnie" activeField={filters.sortField} activeDirection={filters.sortDirection} onSort={handleSort} />
                <SortableHeader field="parking" label="Parking" activeField={filters.sortField} activeDirection={filters.sortDirection} onSort={handleSort} />
                <SortableHeader field="typeIntervention" label="Type" activeField={filters.sortField} activeDirection={filters.sortDirection} onSort={handleSort} />
                <SortableHeader field="priorite" label="Priorite" activeField={filters.sortField} activeDirection={filters.sortDirection} onSort={handleSort} />
                <SortableHeader field="statut" label="Statut" activeField={filters.sortField} activeDirection={filters.sortDirection} onSort={handleSort} />
                <th>Equipe</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {data?.data.length === 0 ? (
                <tr>
                  <td colSpan={9} className="text-center py-12">
                    <ClipboardDocumentListIcon className="w-12 h-12 mx-auto text-gray-300 mb-3" />
                    <p className="text-gray-500 mb-3">Aucune intervention trouvee</p>
                    {canAccess.creationDeneignement && (
                      <Link to="/deneignements/nouveau" className="btn-primary inline-flex items-center">
                        <PlusIcon className="w-5 h-5 mr-2" />
                        Creer une intervention
                      </Link>
                    )}
                  </td>
                </tr>
              ) : (
                data?.data.map((intervention) => (
                  <tr key={intervention.id}>
                    <td className="font-medium text-primary-600">
                      {intervention.numeroOrdre}
                    </td>
                    <td>
                      <div>
                        {new Date(intervention.dateIntervention).toLocaleDateString('fr-FR')}
                      </div>
                      <div className="text-xs text-gray-500">
                        {intervention.heureSouhaitee
                          ? new Date(intervention.heureSouhaitee).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })
                          : '-'}
                      </div>
                    </td>
                    <td>
                      <span className="font-medium">
                        {intervention.compagnie.codeIata}
                      </span>
                    </td>
                    <td>
                      {intervention.parking?.code || '-'}
                    </td>
                    <td>{TYPE_LABELS[intervention.typeIntervention]}</td>
                    <td>
                      <PrioriteBadge priorite={intervention.priorite} size="sm" />
                    </td>
                    <td>
                      <StatusBadge statut={intervention.statut} size="sm" />
                    </td>
                    <td>
                      {intervention.equipes.length > 0
                        ? intervention.equipes.map((e) => e.nom).join(', ')
                        : '-'}
                    </td>
                    <td>
                      <div className="flex items-center space-x-2">
                        <Link
                          to={`/deneignements/${intervention.id}`}
                          className="p-1 text-gray-400 hover:text-primary-500"
                          title="Voir"
                        >
                          <EyeIcon className="w-5 h-5" />
                        </Link>
                        {canAccess.gestionDeneignement && intervention.statut === 'en_attente' && (
                          <>
                            <button
                              onClick={() => openAffectationModal(intervention)}
                              className="p-1 text-gray-400 hover:text-green-500"
                              title="Affecter une equipe"
                            >
                              <UserGroupIcon className="w-5 h-5" />
                            </button>
                            <Link
                              to={`/deneignements/${intervention.id}/modifier`}
                              className="p-1 text-gray-400 hover:text-primary-500"
                              title="Modifier"
                            >
                              <PencilIcon className="w-5 h-5" />
                            </Link>
                          </>
                        )}
                        {canAccess.gestionDeneignement && STATUTS_EQUIPE_MODIFIABLE.includes(intervention.statut) && (
                          <button
                            onClick={() => openAffectationModal(intervention)}
                            className="p-1 text-gray-400 hover:text-green-500"
                            title="Changer l'equipe"
                          >
                            <UserGroupIcon className="w-5 h-5" />
                          </button>
                        )}
                        {intervention.statut === 'validee' && (
                          <button
                            className="p-1 text-gray-400 hover:text-primary-500"
                            title="Telecharger PDF"
                          >
                            <DocumentArrowDownIcon className="w-5 h-5" />
                          </button>
                        )}
                        {canAccess.gestionDeneignement && STATUTS_ANNULABLES.includes(intervention.statut) && (
                          <button
                            onClick={() => openCancelModal(intervention)}
                            className="p-1 text-gray-400 hover:text-orange-500"
                            title="Annuler l'intervention"
                          >
                            <NoSymbolIcon className="w-5 h-5" />
                          </button>
                        )}
                        {isAdmin && (
                          <button
                            onClick={() => openDeleteModal(intervention)}
                            className="p-1 text-gray-400 hover:text-red-600"
                            title="Supprimer definitivement"
                          >
                            <TrashIcon className="w-5 h-5" />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {data && data.pages > 1 && (
          <div className="px-6 py-4 border-t border-gray-200 flex items-center justify-between">
            <p className="text-sm text-gray-500">
              Page {page} sur {data.pages} ({data.total} resultats)
            </p>
            <div className="flex space-x-2">
              <button
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={page === 1}
                className="btn-secondary"
              >
                Precedent
              </button>
              <button
                onClick={() => setPage((p) => Math.min(data.pages, p + 1))}
                disabled={page === data.pages}
                className="btn-secondary"
              >
                Suivant
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Modal d'affectation */}
      {showAffectationModal && selectedIntervention && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex min-h-screen items-center justify-center p-4">
            {/* Overlay */}
            <div
              className="fixed inset-0 bg-black bg-opacity-50 transition-opacity"
              onClick={() => setShowAffectationModal(false)}
            />

            {/* Modal */}
            <div className="relative bg-white rounded-lg shadow-xl max-w-md w-full p-6">
              {/* Header */}
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-gray-900">
                  {isChangementEquipe ? "Changer l'equipe" : 'Affecter une equipe'}
                </h3>
                <button
                  onClick={() => setShowAffectationModal(false)}
                  className="text-gray-400 hover:text-gray-500"
                >
                  <XMarkIcon className="w-6 h-6" />
                </button>
              </div>

              {/* Contenu */}
              <div className="space-y-4">
                <div>
                  <p className="text-sm text-gray-600">
                    Intervention: <span className="font-medium">{selectedIntervention.numeroOrdre}</span>
                  </p>
                  <p className="text-sm text-gray-600">
                    Parking: <span className="font-medium">{selectedIntervention.parking?.code || 'N/A'}</span>
                  </p>
                </div>

                <div>
                  <label className="label">Equipe</label>
                  <select
                    className="input w-full"
                    value={selectedEquipeId || ''}
                    onChange={(e) => setSelectedEquipeId(e.target.value ? Number(e.target.value) : null)}
                  >
                    <option value="">Selectionner une equipe</option>
                    {equipes?.filter(e => e.actif).map((equipe) => (
                      <option key={equipe.id} value={equipe.id}>
                        {equipe.nom} ({equipe.code})
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Actions */}
              <div className="mt-6 flex justify-end space-x-3">
                <button
                  onClick={() => setShowAffectationModal(false)}
                  className="btn-secondary"
                >
                  Annuler
                </button>
                <button
                  onClick={handleAffectation}
                  disabled={!selectedEquipeId || affectationMutation.isPending || changerEquipeMutation.isPending}
                  className="btn-primary disabled:opacity-50"
                >
                  {affectationMutation.isPending || changerEquipeMutation.isPending
                    ? 'Enregistrement...'
                    : isChangementEquipe ? "Changer l'equipe" : 'Affecter'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal d'annulation (R5 : motif obligatoire) */}
      {showCancelModal && selectedIntervention && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex min-h-screen items-center justify-center p-4">
            <div
              className="fixed inset-0 bg-black bg-opacity-50 transition-opacity"
              onClick={() => setShowCancelModal(false)}
            />
            <div className="relative bg-white rounded-lg shadow-xl max-w-md w-full p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-gray-900">
                  Annuler l'intervention
                </h3>
                <button
                  onClick={() => setShowCancelModal(false)}
                  className="text-gray-400 hover:text-gray-500"
                >
                  <XMarkIcon className="w-6 h-6" />
                </button>
              </div>

              <div className="space-y-4">
                <p className="text-sm text-gray-600">
                  Intervention : <span className="font-medium">{selectedIntervention.numeroOrdre}</span>
                </p>
                <div>
                  <label className="label">Motif de l'annulation *</label>
                  <textarea
                    className="input w-full"
                    rows={3}
                    value={cancelMotif}
                    onChange={(e) => setCancelMotif(e.target.value)}
                    placeholder="Ex: Vol annule, parking liberee, erreur de saisie..."
                  />
                </div>
              </div>

              <div className="mt-6 flex justify-end space-x-3">
                <button onClick={() => setShowCancelModal(false)} className="btn-secondary">
                  Retour
                </button>
                <button
                  onClick={() =>
                    selectedIntervention &&
                    cancelMutation.mutate({ id: selectedIntervention.id, motif: cancelMotif })
                  }
                  disabled={!cancelMotif.trim() || cancelMutation.isPending}
                  className="px-4 py-2 rounded-lg bg-orange-600 text-white font-medium hover:bg-orange-700 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {cancelMutation.isPending ? 'Annulation...' : "Confirmer l'annulation"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal de suppression definitive (ROLE_ADMIN uniquement) */}
      <ConfirmModal
        isOpen={showDeleteModal && selectedIntervention !== null}
        onClose={() => setShowDeleteModal(false)}
        onConfirm={() => selectedIntervention && deleteMutation.mutate(selectedIntervention.id)}
        title="Supprimer definitivement l'intervention"
        message={
          selectedIntervention
            ? `L'intervention ${selectedIntervention.numeroOrdre} sera supprimee definitivement, y compris son historique et ses photos. Cette action est irreversible. Confirmer la suppression ?`
            : ''
        }
        confirmLabel="Supprimer"
        cancelLabel="Annuler"
        variant="danger"
        isLoading={deleteMutation.isPending}
      />
    </div>
  );
}

export default DeneignementListPage;
